import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { api } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import {
  ExclamationTriangleIcon,
  EyeIcon,
  PaperAirplaneIcon,
  MapPinIcon,
  CalendarIcon,
  UserIcon,
  TagIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';

interface Incident {
  id: number;
  title: string;
  description: string;
  incident_type: string;
  category?: string;
  subcategory?: string;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  severity?: 'low' | 'medium' | 'high' | 'critical';
  status?: 'reported' | 'investigating' | 'resolved' | 'dismissed' | 'uncompliant' | 'sent_for_investigation';
  district?: string;
  district_id?: number;
  constituency?: string;
  ward?: string;
  center?: string;
  reporter_name?: string;
  reporter_phone?: string;
  created_at: string;
  updated_at?: string;
  attachments?: string[];
  notes?: string;
  [key: string]: any; // Allow additional properties
}

interface IncidentStats {
  total: number;
  reported: number;
  investigating: number;
  resolved: number;
  dismissed: number;
  uncompliant: number;
  sent_for_investigation: number;
  by_priority: {
    low: number;
    medium: number;
    high: number;
    critical: number;
  };
  by_category: {
    [key: string]: number;
  };
}

interface PreviewModalProps {
  incident: Incident | null;
  isOpen: boolean;
  onClose: () => void;
  onSendForInvestigation: (incident: Incident) => void;
  onRejectIncident: (incident: Incident) => void;
}

const PreviewModal: React.FC<PreviewModalProps> = ({ incident, isOpen, onClose, onSendForInvestigation, onRejectIncident }) => {
  if (!isOpen || !incident) return null;

  // Debug: Log the incident object to see its structure
  console.log('🔍 Incident object in PreviewModal:', incident);
  console.log('🔍 Comment1 value:', incident.comment1);
  console.log('🔍 Comment value:', incident.comment);
  console.log('🔍 Comment2 value:', incident.comment2);
  console.log('🔍 Notes value:', incident.notes);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved': return 'text-green-600 bg-green-50 border-green-200';
      case 'investigating': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'reported': return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'sent_for_investigation': return 'text-indigo-600 bg-indigo-50 border-indigo-200';
      case 'uncompliant': return 'text-red-600 bg-red-50 border-red-200';
      case 'dismissed': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />
              <h2 className="text-xl font-bold text-gray-900">Incident Details</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <XCircleIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Title and Priority */}
          <div className="space-y-3">
            <h3 className="text-2xl font-bold text-gray-900">{incident.title}</h3>
            <div className="flex items-center space-x-4">
              <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${getPriorityColor(incident.priority || 'low')}`}>
                {(incident.priority || 'LOW').toUpperCase()}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(incident.status || 'pending')}`}>
                {(incident.status || 'PENDING').toUpperCase()}
              </span>
            </div>
          </div>

          {/* Description */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Description</h4>
            <p className="text-gray-700 leading-relaxed">{incident.description}</p>
          </div>

          {/* Location and Reporter Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Location */}
            <div className="space-y-3">
              <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                <MapPinIcon className="w-5 h-5 mr-2" />
                Location Details
              </h4>
              <div className="space-y-2 text-sm">
                <div><span className="font-medium">District:</span> {incident.district || incident.district_name || 'N/A'}</div>
                {(incident.constituency || incident.constituency_name) && <div><span className="font-medium">Constituency:</span> {incident.constituency || incident.constituency_name}</div>}
                {incident.ward && <div><span className="font-medium">Ward:</span> {incident.ward}</div>}
                {(incident.center || incident.polling_center) && <div><span className="font-medium">Center:</span> {incident.center || incident.polling_center}</div>}
              </div>
            </div>

            {/* Reporter */}
            <div className="space-y-3">
              <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                <UserIcon className="w-5 h-5 mr-2" />
                Reporter Information
              </h4>
              <div className="space-y-2 text-sm">
                <div><span className="font-medium">Name:</span> {incident.reporter_name}</div>
                <div><span className="font-medium">Phone:</span> {incident.reporter_phone}</div>
              </div>
            </div>
          </div>

          {/* Additional Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2 text-sm">
              <div className="flex items-center">
                <TagIcon className="w-4 h-4 mr-2 text-gray-500" />
                <span className="font-medium">Type:</span>
                <span className="ml-1">{incident.incident_type}</span>
              </div>
              <div className="flex items-center">
                <CalendarIcon className="w-4 h-4 mr-2 text-gray-500" />
                <span className="font-medium">Reported:</span>
                <span className="ml-1">{new Date(incident.created_at).toLocaleString()}</span>
              </div>
              <div className="flex items-center">
                <ClockIcon className="w-4 h-4 mr-2 text-gray-500" />
                <span className="font-medium">Updated:</span>
                <span className="ml-1">{incident.updated_at ? new Date(incident.updated_at).toLocaleString() : 'Not updated'}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {incident.notes && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Notes</h4>
              <p className="text-gray-700">{incident.notes}</p>
            </div>
          )}

          {/* Hub Comments (comment1) */}
          <div className="bg-yellow-50 rounded-lg p-4">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Hub Comments</h4>
            <div className="text-gray-700 border-l-4 border-yellow-400 pl-3">
              {incident.comment1 ? (
                <p className="whitespace-pre-wrap">{incident.comment1}</p>
              ) : (
                <p className="text-gray-500 italic">No hub comments available</p>
              )}
            </div>
          </div>

          {/* Intervention Comments (comment2) */}
          <div className="bg-purple-50 rounded-lg p-4">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Intervention Comments</h4>
            <div className="text-gray-700 border-l-4 border-purple-400 pl-3">
              {incident.comment2 ? (
                <p className="whitespace-pre-wrap">{incident.comment2}</p>
              ) : (
                <p className="text-gray-500 italic">No intervention comments available</p>
              )}
            </div>
          </div>

          {/* General Comments */}
          {incident.comment && (
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">General Comments</h4>
              <div className="text-gray-700 border-l-4 border-green-400 pl-3">
                <p className="whitespace-pre-wrap">{incident.comment}</p>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-2xl">
          <div className="flex items-center justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
            >
              Close
            </button>
            {incident.status === 'reported' && (
              <>
                <button
                  onClick={() => onSendForInvestigation(incident)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center space-x-2"
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                  <span>Send for Investigation</span>
                </button>
                <button
                  onClick={() => onRejectIncident(incident)}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center space-x-2"
                >
                  <XCircleIcon className="w-4 h-4" />
                  <span>Uncompliant/Reject</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const IncidentsAction: React.FC = () => {
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>(() => {
    // Load selected districts from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('incidentsAction_selectedDistricts');
      return saved ? JSON.parse(saved) : [];
    }
    return [];
  });
  const [showDistrictFilter, setShowDistrictFilter] = useState(() => {
    // Load filter visibility state from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('incidentsAction_showDistrictFilter');
      return saved ? JSON.parse(saved) : false;
    }
    return false;
  });
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Save selectedDistricts to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('incidentsAction_selectedDistricts', JSON.stringify(selectedDistricts));
    }
  }, [selectedDistricts]);

  // Save showDistrictFilter to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('incidentsAction_showDistrictFilter', JSON.stringify(showDistrictFilter));
    }
  }, [showDistrictFilter]);

  // Fetch districts for filtering
  const { data: complianceData } = useQuery(
    ['district-compliance-for-incidents'],
    () => api.dashboard.getDistrictCompliance(),
    { refetchInterval: 30000 }
  );

  // Fetch all incidents (filter by district on frontend)
  const { data: incidentsData, isLoading: incidentsLoading, refetch: refetchIncidents } = useQuery(
    ['all-incidents-action'],
    () => api.incidents.getAll({
      limit: 100000,
      page: 1,
      per_page: 100000
    }),
    {
      refetchInterval: 30000,
      onSuccess: (data) => {
        console.log('✅ Incidents data received:', data);
      },
      onError: (error) => {
        console.error('❌ Error fetching incidents:', error);
      }
    }
  );

  // Fetch incident stats
  const { data: statsData } = useQuery(
    ['incidents-stats', selectedDistricts],
    () => api.incidents.getStats({ 
      district_id: selectedDistricts.length === 1 ? selectedDistricts[0] : undefined,
      limit: 100000,
      page: 1,
      per_page: 100000
    }),
    { 
      refetchInterval: 30000,
      onSuccess: (data) => {
        console.log('✅ Stats data received:', data);
      },
      onError: (error) => {
        console.error('❌ Error fetching stats:', error);
      }
    }
  );

  const districts = complianceData?.data?.data?.districts || [];
  
  // Debug incidents data structure
  console.log('🔍 Full incidents API response:', incidentsData);
  console.log('🔍 Incidents data path:', incidentsData?.data);
  console.log('🔍 Incidents array:', incidentsData?.data?.data);
  
  // Get all incidents from API
  const allIncidents: Incident[] = incidentsData?.data?.data || 
                                   incidentsData?.data?.incidents || 
                                   incidentsData?.data || 
                                   [];
  
  console.log('🔍 All incidents received:', allIncidents.length);
  
  // Filter ALL incidents by selected districts (for stats calculation)
  const districtFilteredIncidents: Incident[] = selectedDistricts.length > 0
    ? allIncidents.filter(incident => 
        selectedDistricts.some(districtId => 
          incident.district_id == parseInt(districtId) || 
          incident.district === districts.find((d: any) => d.district_id == parseInt(districtId))?.district_name
        )
      )
    : allIncidents;

  // Filter incidents for TABLE display (only "reported" status)
  const incidents: Incident[] = districtFilteredIncidents.filter(incident => incident.status === 'reported');
  
  console.log('🔍 All incidents in selected district:', districtFilteredIncidents.length);
  console.log('🎯 Reported incidents for table:', incidents.length, selectedDistricts.length > 0 ? `(Districts: ${selectedDistricts.map(id => districts.find((d: any) => d.district_id == parseInt(id))?.district_name).join(', ')})` : '(All Districts)');
  
  // Debug: Log the first incident to see its structure
  if (incidents.length > 0) {
    console.log('📋 First incident structure:', incidents[0]);
    console.log('📍 Location fields in first incident:', {
      district: incidents[0].district,
      district_name: incidents[0].district_name,
      constituency: incidents[0].constituency,
      ward: incidents[0].ward,
      center: incidents[0].center,
      polling_center: incidents[0].polling_center
    });
  }
  
  // Calculate category stats based on ONLY the incidents displayed in the table ("reported" status)
  const categoryStats = incidents.reduce((acc, incident) => {
    const category = incident.category;
    // Only include valid categories, exclude unwanted ones
    if (category && !['Administrative', 'Technical', 'Elections', 'Electoral', 'Unknown', 'Governance/HR', 'Health/Environment'].includes(category)) {
      if (!acc[category]) {
        acc[category] = {
          total: 0,
          by_severity: { low: 0, medium: 0, high: 0, critical: 0 }
        };
      }
      acc[category].total += 1;
      const severity = (incident.priority || incident.severity || 'low');
      if (acc[category].by_severity[severity] !== undefined) {
        acc[category].by_severity[severity] += 1;
      }
    }
    return acc;
  }, {} as Record<string, { total: number; by_severity: { low: number; medium: number; high: number; critical: number } }>);
  
  const stats: IncidentStats = {
    total: districtFilteredIncidents.length || 0,
    reported: districtFilteredIncidents.filter(i => i.status === 'reported').length || 0,
    investigating: districtFilteredIncidents.filter(i => i.status === 'investigating').length || 0,
    resolved: districtFilteredIncidents.filter(i => i.status === 'resolved').length || 0,
    dismissed: districtFilteredIncidents.filter(i => i.status === 'dismissed').length || 0,
    uncompliant: districtFilteredIncidents.filter(i => i.status === 'uncompliant').length || 0,
    sent_for_investigation: districtFilteredIncidents.filter(i => i.status === 'sent_for_investigation').length || 0,
    by_priority: { 
      low: districtFilteredIncidents.filter(i => (i.priority || i.severity) === 'low').length || 0,
      medium: districtFilteredIncidents.filter(i => (i.priority || i.severity) === 'medium').length || 0,
      high: districtFilteredIncidents.filter(i => (i.priority || i.severity) === 'high').length || 0,
      critical: districtFilteredIncidents.filter(i => (i.priority || i.severity) === 'critical').length || 0
    },
    by_category: Object.keys(categoryStats).reduce((acc, category) => {
      acc[category] = categoryStats[category].total;
      return acc;
    }, {} as Record<string, number>)
  };
  
  console.log('📊 District-filtered stats:', stats);

  const handleSendForInvestigation = async (incident: Incident) => {
    const confirmed = window.confirm(
      `Are you sure you want to send "${incident.title}" for investigation?`
    );

    if (confirmed) {
      try {
        await api.incidents.updateStatus(
          incident.id.toString(),
          'sent_for_investigation',
          'Incident assigned to field investigator. Initial assessment completed.'
        );
        toast.success('Incident sent for investigation successfully!');
        refetchIncidents();
        setShowPreview(false);
      } catch (error) {
        toast.error('Failed to send incident for investigation');
        console.error('Error updating incident status:', error);
      }
    }
  };

  const handleRejectIncident = async (incident: Incident) => {
    const confirmed = window.confirm(
      `Are you sure you want to mark "${incident.title}" as uncompliant?`
    );

    if (confirmed) {
      try {
        await api.incidents.updateStatus(
          incident.id.toString(),
          'uncompliant',
          'Incident marked as non-compliant after administrative review.'
        );
        toast.success('Incident marked as uncompliant successfully!');
        refetchIncidents();
        setShowPreview(false);
      } catch (error) {
        toast.error('Failed to mark incident as uncompliant');
        console.error('Error updating incident status:', error);
      }
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'resolved': return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
      case 'investigating': return <ExclamationCircleIcon className="w-4 h-4 text-blue-500" />;
      case 'reported': return <ExclamationTriangleIcon className="w-4 h-4 text-purple-500" />;
      case 'sent_for_investigation': return <ExclamationCircleIcon className="w-4 h-4 text-indigo-500" />;
      case 'uncompliant': return <XCircleIcon className="w-4 h-4 text-red-500" />;
      case 'dismissed': return <XCircleIcon className="w-4 h-4 text-gray-500" />;
      default: return <ClockIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  if (incidentsLoading && !incidentsData) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50 to-orange-50">
      <div className="container px-4 py-8 mx-auto sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="relative overflow-hidden bg-white shadow-xl rounded-2xl">
            <div className="absolute inset-0 bg-gradient-to-r from-red-600 via-orange-600 to-yellow-600 opacity-10"></div>
            <div className="relative px-8 py-12 text-center">
              <div className="inline-flex items-center px-4 py-2 mb-4 text-sm font-medium text-red-700 bg-red-100 rounded-full">
                <div className="w-2 h-2 mr-2 bg-red-600 rounded-full animate-pulse"></div>
                Incidents Management
              </div>
              <h1 className="mb-4 text-4xl font-bold text-transparent xl:text-5xl bg-gradient-to-r from-gray-900 via-red-900 to-orange-900 bg-clip-text">
                Incidents Action Center
              </h1>
              <p className="max-w-2xl mx-auto text-lg text-gray-600 xl:text-xl">
                Review and take action on reported incidents across all districts. Send incidents for investigation or reject as uncompliant.
              </p>
            </div>
          </div>
        </div>

        {/* District Filter & Stats */}
        <div className="grid grid-cols-1 gap-6 mb-8 lg:grid-cols-4">
          {/* District Filter */}
          <div className="lg:col-span-1">
            <div className="p-4 bg-white border border-gray-200 rounded-2xl shadow-lg">
              <h3 className="mb-3 text-lg font-semibold text-gray-900 flex items-center justify-between">
                <div className="flex items-center">
                  <FunnelIcon className="w-5 h-5 mr-2" />
                  Filter by Districts
                </div>
                <button
                  onClick={() => setShowDistrictFilter(!showDistrictFilter)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {showDistrictFilter ? 'Hide' : 'Show'}
                </button>
              </h3>
              
              {/* Selected Districts Summary */}
              <div className="mb-3">
                <div className="text-sm text-gray-600">
                  {selectedDistricts.length === 0 ? (
                    'All Districts Selected'
                  ) : (
                    `${selectedDistricts.length} District${selectedDistricts.length > 1 ? 's' : ''} Selected`
                  )}
                </div>
                {selectedDistricts.length > 0 && (
                  <button
                    onClick={() => {
                      setSelectedDistricts([]);
                      setShowDistrictFilter(false);
                      // Also clear from localStorage
                      if (typeof window !== 'undefined') {
                        localStorage.removeItem('incidentsAction_selectedDistricts');
                        localStorage.removeItem('incidentsAction_showDistrictFilter');
                      }
                    }}
                    className="text-xs text-red-600 hover:text-red-800 mt-1"
                  >
                    Clear All
                  </button>
                )}
              </div>
              
              {(showDistrictFilter || selectedDistricts.length > 0) && (
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2">
                  {districts.map((district: any) => (
                    <label key={district.district_id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedDistricts.includes(district.district_id.toString())}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDistricts([...selectedDistricts, district.district_id.toString()]);
                          } else {
                            setSelectedDistricts(selectedDistricts.filter(id => id !== district.district_id.toString()));
                          }
                        }}
                        className="text-red-600 border-gray-300 rounded focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700">{district.district_name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="lg:col-span-3">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="p-4 bg-white border border-purple-200 rounded-xl shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600">Reported</p>
                    <p className="text-2xl font-bold text-purple-700">{stats.reported}</p>
                  </div>
                  <ExclamationTriangleIcon className="w-8 h-8 text-purple-400" />
                </div>
              </div>

              <div className="p-4 bg-white border border-indigo-200 rounded-xl shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-indigo-600">Sent for Investigation</p>
                    <p className="text-2xl font-bold text-indigo-700">{stats.sent_for_investigation}</p>
                  </div>
                  <ExclamationCircleIcon className="w-8 h-8 text-indigo-400" />
                </div>
              </div>

              <div className="p-4 bg-white border border-red-200 rounded-xl shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600">Uncompliant</p>
                    <p className="text-2xl font-bold text-red-700">{stats.uncompliant}</p>
                  </div>
                  <XCircleIcon className="w-8 h-8 text-red-400" />
                </div>
              </div>

              <div className="p-4 bg-white border border-green-200 rounded-xl shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Resolved</p>
                    <p className="text-2xl font-bold text-green-700">{stats.resolved}</p>
                  </div>
                  <CheckCircleIcon className="w-8 h-8 text-green-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Category Stats */}
        <div className="mb-8">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Incidents by Category</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {categoryStats && Object.entries(categoryStats).map(([category, data]) => (
                <div key={category} className="p-4 bg-gray-50 rounded-lg border">
                  <div className="text-sm font-medium text-gray-900 truncate mb-2" title={category}>
                    {category}
                  </div>
                  <div className="text-xl font-bold text-gray-700 mb-3">{data.total}</div>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <div className="flex items-center justify-between bg-red-50 px-2 py-1 rounded border border-red-200">
                      <span className="text-red-600">Critical</span>
                      <span className="font-semibold text-red-700">{data.by_severity.critical}</span>
                    </div>
                    <div className="flex items-center justify-between bg-orange-50 px-2 py-1 rounded border border-orange-200">
                      <span className="text-orange-600">High</span>
                      <span className="font-semibold text-orange-700">{data.by_severity.high}</span>
                    </div>
                    <div className="flex items-center justify-between bg-yellow-50 px-2 py-1 rounded border border-yellow-200">
                      <span className="text-yellow-600">Medium</span>
                      <span className="font-semibold text-yellow-700">{data.by_severity.medium}</span>
                    </div>
                    <div className="flex items-center justify-between bg-blue-50 px-2 py-1 rounded border border-blue-200">
                      <span className="text-blue-600">Low</span>
                      <span className="font-semibold text-blue-700">{data.by_severity.low}</span>
                    </div>
                  </div>
                </div>
              ))}
              {(!categoryStats || Object.keys(categoryStats).length === 0) && (
                <div className="col-span-full text-center py-4 text-gray-500">
                  No category data available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Incidents Table */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Reported Incidents {selectedDistricts.length > 0 && `in ${selectedDistricts.length === 1 ? districts.find((d: any) => d.district_id == parseInt(selectedDistricts[0]))?.district_name : `${selectedDistricts.length} Districts`}`}
            </h3>
            <p className="text-sm text-gray-600">
              {incidents.length} reported incident{incidents.length !== 1 ? 's' : ''} found
              {selectedDistricts.length > 1 && (
                <span className="ml-2 text-xs text-blue-600">
                  across {selectedDistricts.map(id => districts.find((d: any) => d.district_id == parseInt(id))?.district_name).join(', ')}
                </span>
              )}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Incident</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reporter</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {incidents.map((incident) => (
                  <tr key={incident.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{incident.title}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{incident.category || 'Unknown'}</div>
                      {incident.subcategory && (
                        <div className="text-sm text-gray-500 truncate" title={incident.subcategory}>
                          {incident.subcategory.length > 30 ? incident.subcategory.substring(0, 30) + '...' : incident.subcategory}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{incident.district || incident.district_name || 'N/A'}</div>
                      {(incident.center || incident.polling_center) && (
                        <div className="text-sm text-gray-500">{incident.center || incident.polling_center}</div>
                      )}
                      {incident.constituency && (
                        <div className="text-sm text-gray-500">{incident.constituency}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor((incident.priority || incident.severity) || 'low')}`}>
                        {(incident.priority || incident.severity || 'low').charAt(0).toUpperCase() + (incident.priority || incident.severity || 'low').slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(incident.status || 'pending')}
                        <span className="text-sm text-gray-900 capitalize">{incident.status || 'pending'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{incident.reporter_name}</div>
                      <div className="text-sm text-gray-500">{incident.reporter_phone}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {new Date(incident.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            setSelectedIncident(incident);
                            setShowPreview(true);
                          }}
                          className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50"
                          title="Preview"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        {incident.status === 'reported' && (
                          <>
                            <button
                              onClick={() => handleSendForInvestigation(incident)}
                              className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-green-50"
                              title="Send for Investigation"
                            >
                              <PaperAirplaneIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRejectIncident(incident)}
                              className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                              title="Reject as Uncompliant"
                            >
                              <XCircleIcon className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {incidents.length === 0 && (
              <div className="text-center py-12">
                <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No reported incidents found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {selectedDistricts.length > 0 ? 'No reported incidents found for the selected districts.' : 'No incidents with "reported" status have been found.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Preview Modal */}
        <PreviewModal
          incident={selectedIncident}
          isOpen={showPreview}
          onClose={() => {
            setShowPreview(false);
            setSelectedIncident(null);
          }}
          onSendForInvestigation={handleSendForInvestigation}
          onRejectIncident={handleRejectIncident}
        />
      </div>
    </div>
  );
};

export default IncidentsAction;